import React, { FC, useState, KeyboardEventHandler, CSSProperties, createContext } from 'react';
import classnames from 'classnames';
import { Padding } from './sharedTypes';

import styles from './commonList.scss';

// use Context to enable useTab hook to set values without prop drilling
export const TabIndicatorContext = createContext<
    (position: { left?: number; width?: number }) => void
>(() => {});

type Props = {
    role?: 'tablist';
    container?: 'nav' | 'div';
    animate?: boolean;
    onKeyDown?: KeyboardEventHandler;
    underline?: boolean;
    indicatorPadding?: Padding;
    indicatorHeight?: '1px' | '2px';
    contrast?: boolean;
};

const paddingClasses = {
    none: 'paddingNone',
    xSmall: 'paddingXSmall',
    small: 'paddingSmall',
} as const;

export const CommonList: FC<Props> = ({
    role,
    container: Container = 'div',
    animate,
    onKeyDown,
    children,
    underline = true,
    indicatorPadding = 'xSmall',
    indicatorHeight = '2px',
    contrast,
}) => {
    const [{ left, width }, setIndicatorPosition] = useState<{ left?: number; width?: number }>({});
    // only add indicator if position values are valid. this prevents a
    // transition from executing on initial indicator render.
    const indicatorInlineStyle: CSSProperties | undefined = width
        ? {
              display: 'block',
              width: `${width}px`,
              transform: `translate3d(${left}px, 0, 0)`,
              height: indicatorHeight,
          }
        : undefined;

    return (
        <Container className={styles.container}>
            <ul
                className={classnames(styles.tabList, styles[paddingClasses[indicatorPadding]], {
                    [styles.underline]: underline,
                })}
                role={role}
                onKeyDown={onKeyDown}
            >
                <TabIndicatorContext.Provider value={setIndicatorPosition}>
                    {children}
                </TabIndicatorContext.Provider>
            </ul>
            <div
                style={indicatorInlineStyle}
                className={classnames(styles.indicator, {
                    [styles.animate]: animate,
                    [styles.contrast]: contrast,
                })}
            />
        </Container>
    );
};
